/**
 * Whether a bill has an invoice number the AP push will accept, and who gets to say what it is.
 *
 * A payable is identified by its invoice number: it is what tells one invoice from the same invoice
 * pushed twice. bill-automation blocks a push without one, and refuses a value that cannot serve as an
 * identity. This module is that judgement, restated on the client so a push is never offered that the
 * service is going to refuse.
 *
 * **These rules mirror `ApPushIdentity` in bill-automation and must not drift from it.** A guard that is
 * merely "not empty" enables the control for values the service then blocks — the exact failure this
 * exists to remove. Where the two could disagree, the service wins and the person sees a refusal they
 * were given no way to predict.
 */

/** Matches `ApPushIdentity.MinimumInvoiceNumberLength`. */
const MINIMUM_LENGTH = 4;

/** Matches `ApPushIdentity.TrustedParseConfidence`. Strictly greater — 0.90 exactly is not trusted. */
export const TRUSTED_PARSE_CONFIDENCE = 0.9;
const EARLIEST_PLAUSIBLE_YEAR = 1990;
const LATEST_PLAUSIBLE_YEAR = 2099;
const MONTH_LABEL = /^(JANUARY|JAN|FEBRUARY|FEB|MARCH|MAR|APRIL|APR|MAY|JUNE|JUN|JULY|JUL|AUGUST|AUG|SEPTEMBER|SEPT|SEP|OCTOBER|OCT|NOVEMBER|NOV|DECEMBER|DEC)(\d{2}|\d{4})?$/;

/**
 * Only letters and digits carry identity — an allowlist, not a denylist. Any retained punctuation class
 * re-opens the duplicate hole: `INV.0042` and `INV 0042` have to be one invoice, or the same document
 * read twice slips both the fingerprint check and AppFolio's own Reference uniqueness.
 */
export const normalizeInvoiceNumber = value => String(value !== null && value !== void 0 ? value : '').split('').filter(character => /[a-z0-9]/i.test(character)).join('').toUpperCase();
const isYear = digits => {
  const year = Number(digits);
  return year >= EARLIEST_PLAUSIBLE_YEAR && year <= LATEST_PLAUSIBLE_YEAR;
};
const isMonth = digits => Number(digits) >= 1 && Number(digits) <= 12;
const isDay = digits => Number(digits) >= 1 && Number(digits) <= 31;
const looksLikeDigitDate = digits => {
  if (digits.length === 4) return isYear(digits);
  if (digits.length === 6) {
    return isYear(digits.slice(0, 4)) && isMonth(digits.slice(4)) || isMonth(digits.slice(0, 2)) && isYear(digits.slice(2));
  }
  if (digits.length === 8) {
    return isYear(digits.slice(0, 4)) && isMonth(digits.slice(4, 6)) && isDay(digits.slice(6)) || isMonth(digits.slice(0, 2)) && isDay(digits.slice(2, 4)) && isYear(digits.slice(4)) || isDay(digits.slice(0, 2)) && isMonth(digits.slice(2, 4)) && isYear(digits.slice(4));
  }
  return false;
};
const looksLikeADate = normalized => MONTH_LABEL.test(normalized) || /^\d+$/.test(normalized) && looksLikeDigitDate(normalized);

/** Every account number the bill carries: the bill's own, and each property's. */
const accountNumbersOf = extraction => [extraction === null || extraction === void 0 ? void 0 : extraction.accountNumber, ...((extraction === null || extraction === void 0 ? void 0 : extraction.properties) || []).map(property => property === null || property === void 0 ? void 0 : property.accountNumber)].map(normalizeInvoiceNumber).filter(Boolean);

/**
 * Whether a value can identify an invoice at all. The account-number rule is the one that surprises
 * people: an account number repeats on every statement a biller sends, so a payable filed under one
 * could not be told from next month's, and AppFolio would refuse all but the first.
 */
export const isUsableInvoiceNumber = (value, extraction) => {
  const normalized = normalizeInvoiceNumber(value);
  if (normalized.length < MINIMUM_LENGTH || looksLikeADate(normalized)) return false;
  return !accountNumbersOf(extraction).includes(normalized);
};

/**
 * The invoice number the extraction itself offers, or null when it has none it could use — which is
 * also the answer when it read something that cannot serve as one.
 */
export const getExtractedInvoiceNumber = extraction => {
  var _ref, _ref2, _extraction$invoiceNu;
  const printed = (_ref = (_ref2 = (_extraction$invoiceNu = extraction === null || extraction === void 0 ? void 0 : extraction.invoiceNumber) !== null && _extraction$invoiceNu !== void 0 ? _extraction$invoiceNu : extraction === null || extraction === void 0 ? void 0 : extraction.invoiceNo) !== null && _ref2 !== void 0 ? _ref2 : extraction === null || extraction === void 0 ? void 0 : extraction.invoice) !== null && _ref !== void 0 ? _ref : null;
  return isUsableInvoiceNumber(printed, extraction) ? String(printed) : null;
};

/**
 * Whether the extraction's own invoice number stands, leaving nothing for a person to enter.
 *
 * Confidence is what separates "the document says so" from "the document was read badly": below the
 * bar, or with none recorded — which is not a claim of accuracy — the person holding the bill outranks
 * the extractor and may correct it.
 *
 * `parseConfidence` is DOCUMENT-level. There is no per-field confidence, so a bill can sit at 0.98 and
 * still have misread this particular value; that is the trade the threshold makes.
 */
export const isInvoiceNumberFixed = extraction => getExtractedInvoiceNumber(extraction) !== null && Number(extraction === null || extraction === void 0 ? void 0 : extraction.parseConfidence) > TRUSTED_PARSE_CONFIDENCE;

/**
 * What the push should carry, and whether it may go at all.
 *
 * `invoiceNumber` is sent only when it is this side's contribution — a fixed extraction identifies
 * itself, and re-sending its number would just be ignored.
 */
export const resolveApPushInvoiceNumber = _ref3 => {
  let extraction = _ref3.extraction,
    typedInvoiceNumber = _ref3.typedInvoiceNumber;
  if (isInvoiceNumberFixed(extraction)) {
    return {
      canPush: true,
      invoiceNumber: undefined,
      reason: null
    };
  }
  const typed = String(typedInvoiceNumber !== null && typedInvoiceNumber !== void 0 ? typedInvoiceNumber : '').trim();
  if (typed) {
    return isUsableInvoiceNumber(typed, extraction) ? {
      canPush: true,
      invoiceNumber: typed,
      reason: null
    } : {
      canPush: false,
      invoiceNumber: undefined,
      reason: 'That invoice number cannot identify an invoice. It needs at least four letters or digits, ' + 'and cannot be a date or the account number.'
    };
  }

  // Nothing typed: an untrusted extraction can still stand on its own number if it has one.
  const extracted = getExtractedInvoiceNumber(extraction);
  return extracted ? {
    canPush: true,
    invoiceNumber: undefined,
    reason: null
  } : {
    canPush: false,
    invoiceNumber: undefined,
    reason: 'This bill has no invoice number. Add one before pushing it to AppFolio.'
  };
};